/**
 * @fileoverview ComputationCmds.js - Commands for docking and energies
 *
 * @typedef {import('BMapsModel').Atom} Atom
 * @typedef {import('BMapsModel').AtomGroup}
 * @typedef {import('./GiFEHelpers').BMapsGiFERequest} BMapsGiFERequest
 * @typedef {import('./GiFEHelpers').GiFEResultUnit} GiFEResultUnit
 */
import _ from 'lodash';
import { EnergyInfo, Compound, CompoundHeritage } from 'BMapsModel';
import { ensureArray } from 'BMapsSrc/util/js_utils';
import { getMapLightProperties } from 'BMapsSrc/integrations/maplight';
import { getGninaScoringProperties, startGninaDocking } from 'BMapsSrc/integrations/gnina';
import {
    startFepScoring, applyFepScoringResults, MIN_COMPOUNDS_FOR_DG,
} from 'BMapsSrc/integrations/fep';
import { waitForJobToComplete, JobTypes } from 'BMapsSrc/jobman';
import { postJob } from 'BMapsSrc/jobman/JobsApi';
import {
    findLibrary, findScreenType, getLibraryOptions, screenTypeOptions,
} from 'BMapsSrc/jobman/screeningLibraries';
import { ReinventJob } from 'BMapsSrc/jobman/jobs/ReinventJob';
import { loadResult, removeJob, createJob } from 'BMapsRedux/jobs/access';
import { MolDataSource, MoleculeLoadOptions } from 'BMapsSrc/utils';
import { App } from '../BMapsApp';
import { EventBroker } from '../eventbroker';
import { ResponseIds } from '../server/server_common';
import { UserActions, UserCmd } from './UserCmd';
import { hasPreviewModeError, showAlert, joinCompoundSpecs } from '../utils';
import { calculateDockingBox } from '../data_tools';
import { reportClashingCompounds } from './ConnectedDataCmds';
import {
    integrateCompounds, integrateEnergies, integrateForcefieldParams,
    doWithActiveSolute, updateGifeEnergiesForCompounds,
    gatherEnergyErrors, filterNonCompounds,
} from './cmds_common';
import { getPreferences } from '../redux/prefs/access';
import { sendGiFERequest, prepareGiFERequest } from './GiFEHelpers';
import { Loader } from '../Loader';

/**
 * @typedef ComputationCmds
 * @type {object}
 * @property {submitDocking} SubmitDocking
 * @property {energyMinimize} EnergyMinimize
 * @property {getEnergies} GetEnergies
 * @property {getSolvation} GetSolvation
 * @property {getForcefieldParameters} GetForcefieldParameters
 * @property {getGifeEnergies} GetGifeEnergies
 * @property {updateGifeEnergies} UpdateGifeEnergies
 * @property {updateCompoundExternalProps} UpdateCompoundExternalProps
 * @property {dockWithJobManager} DockWithJobManager
 * @property {startFepScoringCmd} StartFepScoring
 * @property {applyFepScoringResultsCmd} ApplyFepScoring
 */

/** @type {ComputationCmds} */
export const ComputationCmds = {
    SubmitDocking: new UserCmd('SubmitDocking', submitDocking),
    EnergyMinimize: new UserCmd('EnergyMinimize', energyMinimize),
    GetEnergies: new UserCmd('GetEnergies', getEnergies),
    GetForcefieldParameters: new UserCmd('GetForcefieldParameters', getForcefieldParameters),
    GetSolvation: new UserCmd('GetSolvation', getSolvation),
    // noEffect: a read (request, return the result; it stores nothing but a cache of the
    // compound's unbound conformation, which undo has no reason to see). The pi-pi display asks
    // for it on every redraw, so without this, an undo that repaints (hydrogens, say) discarded
    // the redo it had just made possible.
    GetGifeEnergies: new UserCmd('GetGifeEnergies', getGifeEnergies, { noEffect: true }),
    UpdateGifeEnergies: new UserCmd('UpdateGifeEnergies', updateGifeEnergies),
    UpdateCompoundExternalProps: new UserCmd('UpdateCompoundExternalProps', updateCompoundExternalProps),
    DockWithJobManager: new UserCmd('DockWithJobManager', dockWithJobManager),
    StartFepScoring: new UserCmd('StartFepScoring', startFepScoringCmd),
    StartReinventGeneration: new UserCmd('StartReinventGeneration', startReinventGenerationCmd),
    GetMapLightPredictions: new UserCmd('GetMapLightPredictions', getMapLightPredictionsCmd),
    StartLibraryScreen: new UserCmd('StartLibraryScreen', startLibraryScreenCmd),
    GetGninaScores: new UserCmd('GetGninaScores', getGninaScoresCmd),
    PredictGninaScores: new UserCmd('PredictGninaScores', predictGninaScoresCmd),
    ApplyFepScoring: new UserCmd('ApplyFepScoring', applyFepScoringResultsCmd),
};

const defaultEnergyBatchSize = 1;
const failoverToAEnergy = false;

/**
 * Ask the server to calculate the forcefield parameters.
 * This happens automatically in the server during minimization, but not for getEnergies.
 * The only known usage for this is "getEnergies" on ligands imported directly from the PDB.
 * This will not create new compounds, and we don't have to do anything with the results.
 * Instead, the forcefield params are processed, applied, and stored by decoder.js.
 * @param {Compound|Compound[]} compoundsIn
 * @returns {{compounds: Compound[], errors: string[]}} Affected compounds and errors
 *
 * @todo Change decoder.js to not directly modify atoms in decodeForcefieldParamsForLigand
 */
async function getForcefieldParameters(compoundsIn) {
    const { Forcefield } = getPreferences();
    const compounds = [].concat(compoundsIn); // ensure array

    const { errorResult, caseData, connector } = getDataParentsForCompounds(compounds, 'Forcefield Parameters');
    if (errorResult) {
        return errorResult;
    }

    const successfulCmpds = [];
    const errors = [];
    // This server API operates one ligand at a time, so loop in case of multiples...
    for (const compound of compounds) {
        compound.energyInfo.forcefieldRequested();
        // The Selector reads anyEnergiesWorking() at render and only re-renders on these
        // events, so every status change here needs one. summarize() publishes nothing.
        EventBroker.publish('energyCalc');
        const requestObj = {
            ligand_spec: compound.resSpec,
            forcefield: Forcefield,
        };
        try {
            const responseData = await doWithActiveSolute(
                caseData, () => connector.cmdGetForcefieldParamsForLigand(requestObj)
            );
            const integrationResults = integrateForcefieldParams(responseData, compound);
            successfulCmpds.push(...integrationResults.compounds);
            errors.push(...integrationResults.errors);
        } catch (ex) {
            const msg = `Forcefield parameter request failed for ${compound.resSpec}: ${ex?.message || ex}`;
            compound.energyInfo.updateForcefieldRequest(false, msg);
            errors.push(msg);
            // Later compounds were never marked working, so stopping leaves nothing spinning.
            break;
        } finally {
            // Defensive, and unreachable today: integrateForcefieldParams settles a
            // still-working status itself, and a throw settles in the catch above.
            if (compound.energyInfo.forcefieldParamStatus === EnergyInfo.States.working) {
                compound.energyInfo.updateForcefieldRequest(
                    false, `No forcefield parameters returned for ${compound.resSpec}`
                );
            }
            EventBroker.publish('energyCalc');
        }
    }
    return { compounds: successfulCmpds, errors };
}

/**
 * Send one or more compounds to the server for docking and return the docked poses
 *
 * @param {Compound[]} compoundsIn - A list of compound objects
 * @param {{
 *  speedArg: "fast" | "faster"
 *  dockingProgram: "autodock" | "diffdock"
 *  poseCount: number
 *  boxParams: *
 *  refObj: *
 *  size: number
 *  keepHs: *
 *  keepSrc3D: *
 * }} dockingParams - An object containing the following fields:
 *      - speedArg - fast | faster
 *      - poseCount - number of poses to return from docking
 *      - boxParams - used by calculatingDockingBox to compute box dimensions:
 *      -- refObj - DockingReference of a specific ligand, hotspot or protein
 *      -- size - edge size
 * @returns {Promise<{
 *  compounds: Compound[]
 *  errors: string[]
 * }>}
 * - compounds {Array<Compound>} - docked poses
 * - errors {Array<String>} - errors
 */
async function submitDocking(compoundsIn, dockingParams={}, otherOptions={}) {
    // `dockingParams` had no default while every field inside it does, so a caller that
    // supplied none -- a workflow step with no parameters, which the dispatcher then omits
    // entirely rather than passing `{}` -- threw
    //     Cannot read properties of undefined (reading 'dockingProgram')
    // before reaching any docking code. Defaulting it lets the per-field defaults below do
    // the job they were written for.
    const { dockingProgram='autodock', poseCount } = dockingParams;
    const { skipGrouping } = otherOptions;
    const compounds = [].concat(compoundsIn); // ensure array

    const { errorResult, caseData, connector } = getDataParentsForCompounds(compounds, 'Docking', false);
    if (errorResult) {
        return errorResult;
    }

    let compoundArgs = [];
    if (dockingProgram === 'autodock') {
        const { speedArg, boxParams } = dockingParams;
        // Autodock currently expects single compound at a time. Docking multiple compounds is
        // implemented as multiple docking requests with a single compound each.
        compoundArgs = compounds.map((c) => {
            const spec = c.resSpec;
            const boxResult = calculateDockingBox(App.Workspace, c, boxParams);
            const coords = boxResult?.coordsList || [0, 0, 0, 0, 0, 0]; // will fail
            const box = coords.map((n) => n.toFixed(2));
            return {
                dockingProgram,
                compoundSpecs: [spec],
                poseCount,
                params: `-box "${box.join(' ')}" -speed ${speedArg}`,
            };
        });
    } else if (dockingProgram === 'diffdock') {
        const {
            keepHs, keepSrc3D,
        } = dockingParams;
        // DiffDock can handle multiple compounds in a single request
        compoundArgs = [{
            dockingProgram,
            compoundSpecs: compounds.map((c) => c.resSpec),
            poseCount,
            params: JSON.stringify({
                keepHs, keepSrc3D,
            }),
        }];
    } else {
        return { compounds: [], errors: [`${dockingProgram} is not a docking program.`] };
    }

    const responseData = await doWithActiveSolute(
        caseData, () => connector.cmdDockCompound(compoundArgs)
    );
    const newCompounds = responseData.byId(ResponseIds.DockResults);
    const errors = responseData.byId(ResponseIds.Error);
    const [dockResultsInfo] = responseData.byId(ResponseIds.DockResultsInfo);
    let dockingResultInfo = dockResultsInfo?.dockingResultInfo || [];

    if (newCompounds.length === 0) {
        return { compounds: newCompounds, errors };
    }

    App.Workspace.addReplaceCompounds(newCompounds, caseData);
    // Activate before integrating: integrateCompounds() triggers the redisplay, which must
    // see the final active compound (matters for visualizers that render asynchronously).
    App.Workspace.activateFirstOf(newCompounds);
    integrateCompounds(responseData, caseData);

    // Workaround for old docking protocol on Windows builds. Massage data from object into array.
    function needOldWindowsWorkaround(result) { return !Array.isArray(result); }
    function oldWindowsWorkaround(result) {
        return ([{
            dockingProgram: 'autodock',
            cmpdSpec: result.autodock.cmpdSpec,
            proteinSpec: null,
            poses: result.autodock.poses,
            warning: null,
        }]);
    }
    if (needOldWindowsWorkaround(dockingResultInfo)) {
        dockingResultInfo = oldWindowsWorkaround(dockingResultInfo);
    }
    // Process dockResultsInfo to add docking score and compound heritage to each pose compound
    // dockResultsInfo is of the form:
    // [
    //     {
    //         dockingProgram,
    //         proteinSpec,
    //         cmpdSpec,
    //         poses: [
    //             {
    //                 poseName,
    //                 <other fields specific to the docking program>...
    //             },
    //             ...
    //         ],
    //         warning
    //     },
    //     ...
    // ]

    const poseResults = {}; // used for grouping { proteinSpec: { origCmpdSpec: { poses: [] } } }
    for (const {
        dockingProgram: dockProg, proteinSpec, cmpdSpec: origCmpdSpec, poses: poseList, warning,
    } of dockingResultInfo) {
        if (poseList.length === 0) continue;
        if (warning) {
            console.warn(`Docking program ${dockProg} returned a warning for complex ${proteinSpec}-${origCmpdSpec}: ${warning}`);
        }
        const origCmpd = Compound.findBySpec(origCmpdSpec, compounds);
        if (!origCmpd) {
            console.error(`Processing dock results info: failed to find original compound ${origCmpdSpec}`);
            continue;
        }

        // Prepare to collect pose compounds for grouping
        let poseResultsEntry = poseResults[proteinSpec];
        if (!poseResultsEntry) {
            poseResultsEntry = {};
            poseResults[proteinSpec] = poseResultsEntry;
        }
        poseResultsEntry = poseResults[proteinSpec][origCmpdSpec];
        if (!poseResultsEntry) {
            poseResultsEntry = { poses: [] };
            poseResults[proteinSpec][origCmpdSpec] = poseResultsEntry;
        }

        // As we process poses, bestDockingPoseInfo is used flexibly by different docking programs.
        let bestDockingPoseInfo;
        for (const [poseI, poseInfo] of poseList.entries()) {
            const { poseName } = poseInfo;
            const poseCmpd = Compound.findBySpec(poseName, newCompounds);
            if (!poseCmpd) {
                console.error(`Processing dock results info: failed to find pose compound ${poseName}`);
                continue;
            }
            // Remember pose for grouping later
            poseResultsEntry.poses.push(poseCmpd);

            // Update pose compound heritage
            let heritageDetailObj = { dockingProgram: dockProg };

            if (dockProg === 'autodock') {
                const { speedArg, boxParams } = dockingParams;
                const { scores } = poseInfo;
                // Now add the docking score and heritage entry
                const propAddress = EnergyInfo.EnergyTypeProps[EnergyInfo.ExtraTypes.dockingScore];
                poseCmpd.setProperty(propAddress.scope, propAddress.propName, scores.score);
                heritageDetailObj = {
                    ...heritageDetailObj,
                    speedArg,
                    poseCount,
                    boxParams,
                    dockingScore: scores.score,
                    dockingPoseRank: `${poseI+1}/${poseList.length}`,
                };

                if (poseI === 0) { // best pose is first
                    bestDockingPoseInfo = scores.score;
                } else {
                    heritageDetailObj.dockingScoreDelta = scores.score - bestDockingPoseInfo;
                    heritageDetailObj.rmsdFromBestUB = scores['rmsd.ub'];
                    heritageDetailObj.rmsdFromBestLB = scores['rmsd.lb'];
                }
            } else if (dockProg === 'diffdock') {
                const { filename='' } = poseInfo;
                const re = /rank(?<rank>\d+)(_confidence(?<confidence>-?[0-9.]+))?\.sdf/;
                const match = filename.match(re);
                heritageDetailObj = {
                    ...heritageDetailObj,
                };
                if (match) {
                    const rank = Number(match.groups.rank);
                    const confidence = Number(match.groups.confidence);
                    heritageDetailObj.rank = `${rank}/${poseList.length}`;
                    heritageDetailObj.confidence = confidence;
                    if (rank === 1) {
                        bestDockingPoseInfo = confidence;
                    } else if (rank > 1) {
                        heritageDetailObj.confDelta = Math.abs(confidence - bestDockingPoseInfo);
                    }
                }
            }

            poseCmpd.copyScopedPropertiesFrom(origCmpd, { skipPerPose: true, operation: 'dock' });
            CompoundHeritage.addDock(origCmpd, poseCmpd, heritageDetailObj);
        }
    }

    // Put dock results into groups
    const groups = skipGrouping ? [] : groupDockedCompounds(dockingProgram, compounds, poseResults);

    return { compounds: newCompounds, errors, groups };
}

/**
 * Perform docking via the job manager and automatically load the results into the workspace.
 * Like autodock/diffdock, this batches compound loading to avoid adding them one at a time.
 * @param {Compound[]} compoundsIn - compounds to dock
 * @param {object} dockingParams - docking parameters (speedArg, boxParams, poseCount, etc.)
 * @param {object} otherOptions - additional options
 * @returns {Promise<{ compounds: Compound[], errors: string[], groups: any[] }>}
 */
async function dockWithJobManager(compoundsIn, dockingParams, otherOptions={}) {
    const { dockingProgram } = dockingParams;
    if (dockingProgram !== 'gnina') {
        return { compounds: [], errors: [`${dockingProgram} is not a docking program.`] };
    }
    const { skipGrouping } = otherOptions;
    const originalCompounds = ensureArray(compoundsIn);

    // Submit the gnina docking job
    const jobStartBatches = await startGninaDocking(originalCompounds, dockingParams);
    const errors = [];
    const poseResults = {};
    const loadedBatches = [];

    async function cleanupJob(jobid) {
        const removed = await removeJob(jobid);
        if (!removed.results?.success) {
            console.warn(`Failed to delete job ${jobid}. Error: ${removed.error}`);
        }
    }

    for (const {
        jobResults: { results: jobStartInfo, error: jobStartError },
        dockedCompounds,
    } of jobStartBatches) {
        if (jobStartError) {
            errors.push(jobStartError);
            continue;
        }

        if (!jobStartInfo?.jobid) {
            errors.push('Job submission failed: no job ID returned');
            continue;
        }
        const { jobid } = jobStartInfo;

        // Wait for the job to complete
        await waitForJobToComplete(jobid);

        // This docking loads automatically so bail out if the destination casedata has been zapped
        const caseData = dockedCompounds[0]?.getCaseData();
        if (!caseData || !App.Workspace.allCaseData().includes(caseData)) {
            console.warn(`dockWithJobManager: case data for docked compounds from job ${jobid} is missing or has been removed.`);
            errors.push(`Missing CaseData: ${caseData?.getName() || ''}`);
            await cleanupJob(jobid);
            continue;
        }

        const { results: { poses }, error: jobLoadError } = await loadResult(jobid);

        // Remove job up front to avoid leaving anything in the jobs table.
        await cleanupJob(jobid);

        if (jobLoadError) {
            errors.push(jobLoadError);
            continue;
        }

        // Load all compounds from SDF at once
        const molSource = new MolDataSource({
            sourceType: MolDataSource.Types.GninaDocking,
            sourceId: `${jobid}_all_poses`,
            molFormat: 'sdf',
            molData: poses.map((p) => p.sdf).join(''),
        });

        const moleculeLoadOptions = MoleculeLoadOptions.NoAlignment;
        // Get connector from the source compounds sent for docking
        const { connector } = App.getDataParents(dockedCompounds[0]);
        const responseData = await doWithActiveSolute(
            dockedCompounds[0], () => connector.cmdLoadMolData(molSource, moleculeLoadOptions)
        );
        const loadedCompounds = responseData.byId(ResponseIds.Compound);
        const compoundMetadatas = responseData.byId(ResponseIds.CompoundMetadata);
        const loadErrors = responseData.byId(ResponseIds.Error);

        if (loadErrors.length > 0) {
            errors.push(...loadErrors);
        }

        if (loadedCompounds.length === 0) {
            errors.push(`Failed to load docked poses from SDF for job ${jobid}`);
            continue;
        }

        loadedBatches.push({
            responseData,
            caseData,
            loadedCompounds,
        });

        // Process each pose: match to loaded compound by tmpI prop and set properties/heritage
        for (const [poseI, pose] of poses.entries()) {
            // To proceed, we need:
            // * The original docked compound for this pose (via sourceSpec)
            // * The loaded compound corresponding to this pose (via tmpI SDF property in metadata)
            const { sourceSpec } = pose;
            const origCmpd = Compound.findBySpec(sourceSpec, originalCompounds);
            if (!origCmpd) {
                console.warn(`dockWithJobManager: could not find original compound for ${sourceSpec}`);
                errors.push(`Pose spec doesn't match an original docked compound: ${sourceSpec}`);
                continue;
            } else if (!App.Workspace.getLoadedCompounds().includes(origCmpd)) {
                console.warn(`dockWithJobManager: docked compound ${sourceSpec} has been removed, `);
            }

            // Peek into metadata to get temporary props to match the pose info to loaded compound.
            // Do it this way because SDF props aren't added to cmpd until integrateCompounds below.
            const foundMeta = compoundMetadatas.find((cm) => (
                cm.scopedProperties?.getPropertyValue('sdfProps', 'tmpSpec') === sourceSpec
                && cm.scopedProperties?.getPropertyValue('sdfProps', 'tmpI') === `${poseI}`
            ));

            if (!foundMeta) {
                errors.push(`Failed to find compoundSpec for pose ${poseI} of ${sourceSpec}`);
                continue;
            }

            // Remove the temporary properties used for matching
            foundMeta.scopedProperties.removeProperty('sdfProps', 'tmpI');
            foundMeta.scopedProperties.removeProperty('sdfProps', 'tmpSpec');

            const compoundSpec = foundMeta.compoundSpec;

            const poseCmpd = Compound.findBySpec(compoundSpec, loadedCompounds);
            if (!poseCmpd) {
                errors.push(`Failed to find loaded compound for compoundSpec ${compoundSpec}`);
                continue;
            }

            // Set gnina properties
            const propertyScope = 'gnina_scoring';
            for (const propName of ['affinity', 'cnn_score', 'cnn_affinity', 'cnn_vs', 'cnn_variance']) {
                if (pose[propName] != null) {
                    poseCmpd.setProperty(propertyScope, propName, pose[propName]);
                }
            }
            poseCmpd.setProperty(propertyScope, 'sourceSpec', sourceSpec);

            // Copy scoped properties from original compound
            poseCmpd.copyScopedPropertiesFrom(origCmpd, { skipPerPose: true, operation: 'dock' });

            // Build heritage detail object
            const heritageDetailObj = {
                dockingProgram: 'gnina',
                speedArg: dockingParams.speedArg,
                poseCount: dockingParams.poseCount,
                boxParams: dockingParams.boxParams,
                dockingScore: pose.affinity,
                gninaAffinity: pose.cnn_affinity,
                gninaPoseScore: pose.cnn_score,
                gninaVS: pose.cnn_vs,
            };

            CompoundHeritage.addDock(origCmpd, poseCmpd, heritageDetailObj);

            // Build poseResults for grouping
            // multi protein docking not yet supported
            // 'protein' is the same with autodock and diffdock
            const proteinSpec = 'protein';
            const origCmpdSpec = origCmpd.resSpec;
            if (!poseResults[proteinSpec]) {
                poseResults[proteinSpec] = {};
            }
            if (!poseResults[proteinSpec][origCmpdSpec]) {
                poseResults[proteinSpec][origCmpdSpec] = { poses: [] };
            }
            poseResults[proteinSpec][origCmpdSpec].poses.push(poseCmpd);
        }
    }

    // Add all compounds to workspace at once, then group
    for (const { caseData, loadedCompounds } of loadedBatches) {
        App.Workspace.addReplaceCompounds(loadedCompounds, caseData);
    }
    // Activate before integrating: integrateCompounds() triggers the redisplay, which must
    // see the final active compound (matters for visualizers that render asynchronously).
    const newCompounds = loadedBatches.flatMap((b) => b.loadedCompounds);
    App.Workspace.activateFirstOf(newCompounds);
    for (const { responseData, caseData } of loadedBatches) {
        // Note: by default, gnina docking results will have gnina_scoring props requested.
        // This could be avoided by passing `{ skipProperties: ['gnina_scoring'] }` into
        // integrateCompounds.
        // However, this would cause the gnina docking poses to not have a gnina stress value.
        // That would require parsing gnina's stdout in the job runner.
        integrateCompounds(responseData, caseData);
    }

    // Group docked compounds
    const groups = skipGrouping ? [] : groupDockedCompounds('gnina', originalCompounds, poseResults);

    const cancelled = errors.length > 0 && errors.every((e) => e.startsWith('Missing CaseData'));
    return {
        compounds: newCompounds, errors, groups, cancelled,
    };
}

/**
 * Put docked compounds into groups. The group structure and naming
 * is based on the number of proteins and compounds.
 */
function groupDockedCompounds(dockingProgram, requestedCompounds, poseResults) {
    // Tree structure:
    // 1 compound-1protein case: (1 group level)
    // - "<docking program> results for <cmpd spec>"
    //   - pose1
    //   - pose2...
    // many compounds-1protein case: (2 group levels)
    // - "<docking program> results for <num> compounds"
    //   - <cmpd spec>
    //     - pose1
    //     - pose2...
    //   - <cmpd spec>
    //     - pose1
    //     - pose2...
    // 1 compound-many proteins case: (2 group levels)
    // - "<docking program> results for <cmpd spec> against <num> proteins"
    //   - <protein spec>
    //     - pose1
    //     - pose2...
    //   - <protein spec>
    //     - pose1
    //     - pose2...
    //   ...
    // Many compounds-many proteins: (3 group levels)
    // - "Dock results for <num> cmpds against <num> proteins"
    //   - <protein spec>
    //     - <cmpd spec>
    //       - pose1
    //       - pose2...
    //     - <cmpd spec>
    //       - pose1
    //       - pose2...
    //   - <protein spec>
    //     - <cmpd spec>
    //     ...
    let groupProgram;
    switch (dockingProgram) {
        case 'autodock': groupProgram = 'Autodock'; break;
        case 'diffdock': groupProgram = 'DiffDock'; break;
        case 'gnina': groupProgram = 'Gnina'; break;
        default: groupProgram = 'Dock';
    }

    const createdGroups = [];

    const proteinCount = Object.keys(poseResults).length;
    const totalPoseCount = Object.values(poseResults).reduce((sum, proteinEntry) => (
        sum + Object.values(proteinEntry).reduce((subsum, { poses }) => subsum + poses.length, 0)
    ), 0);
    if (totalPoseCount === 0) {
        return createdGroups; // nothing to group
    }
    const cmpdCount = requestedCompounds.length;
    let groupLevels;
    let groupType;
    switch (true) {
        case proteinCount > 1 && cmpdCount > 1: groupLevels = 3; groupType = 'manyCmpd-manyProtein'; break;
        case proteinCount > 1: groupLevels = 2; groupType = '1Cmpd-manyProtein'; break;
        case cmpdCount > 1: groupLevels = 2; groupType = 'manyCmpd-1Protein'; break;
        default: groupLevels = 1; groupType = '1Cmpd-1Protein';
    }

    const parentGroups = [];
    for (const [proteinSpec, proteinEntry] of Object.entries(poseResults)) {
        const poseGroups = [];
        for (const [origCmpdSpec, { poses }] of Object.entries(proteinEntry)) {
            const treeItems = App.Workspace.getTreeItemsForItems('Compounds', poses);

            let poseGroupName = '';
            switch (groupType) {
                case '1Cmpd-1Protein': poseGroupName = `${groupProgram} results for ${origCmpdSpec}`; break;
                case 'manyCmpd-1Protein': case 'manyCmpd-manyProtein': poseGroupName = origCmpdSpec; break;
                case '1Cmpd-manyProtein': poseGroupName = proteinSpec; break;
                default: // should be handled already
            }
            const poseGroup = UserActions.GroupItems(treeItems, 'Compounds', undefined, { groupName: poseGroupName });
            poseGroups.push(poseGroup);
            createdGroups.push(poseGroup);
        }
        let parentGroupName = '';
        if (groupLevels > 1) {
            switch (groupType) {
                case 'manyCmpd-1Protein': parentGroupName = `${groupProgram} results for ${cmpdCount} cmpds`; break;
                case '1Cmpd-manyProtein': parentGroupName = `${groupProgram} results for ${requestedCompounds[0].resSpec} against ${proteinCount} proteins`; break;
                case 'manyCmpd-manyProtein': parentGroupName = proteinSpec; break;
                default: // Won't do anything for 1cmpd-1protein
            }
            const parentGroup = UserActions.GroupItems(poseGroups, 'Compounds', undefined, { groupName: parentGroupName });
            parentGroups.push(parentGroup);
            createdGroups.push(parentGroup);
        }
    }
    if (groupLevels > 2) {
        const grandparentGroupName = `${groupProgram} results for ${cmpdCount} cmpds against ${proteinCount} proteins`;
        const grandparentGroup = UserActions.GroupItems(parentGroups, 'Compounds', undefined, { groupName: grandparentGroupName });
        createdGroups.push(grandparentGroup);
    }
    return createdGroups;
}

/**
 * Get data parents (conector and caseData) for the compounds.
 * Currently restricted that the compounds should all be from a single caseData.
 * @param {Compound[]} compounds
 * @param {string} verb Used for error message
*/
function getDataParentsForCompounds(compounds=[], verb, doAlert=true) {
    if (compounds.length === 0) {
        const msg = `${verb} requires at least one compound. Try selecting a compound in the left-hand selector.`;
        if (doAlert) showAlert('Select a Compound', msg);
        return { errorResult: { compounds: [], errors: [msg] } };
    }

    const { dataParentsList } = App.partitionByDataParents(compounds);

    if (dataParentsList.length > 1) {
        const msg = `${verb} compounds from multiple protein cases at the same time is not yet supported.`;
        if (doAlert) showAlert('Multiple Proteins', msg);
        return { errorResult: { compounds: [], errors: [msg] } };
    }

    return dataParentsList[0];
}

// Request both Energies and Solvation
// This operation isn't standard and is not available in production.
// One reason is that the operation text says: "minimizing" and
// it's explicitly NOT being minimized. The language about differenting
// the operations should be clarified for broader use.
async function getEnergies(compoundsIn, options={}) {
    const { EnergyEngine, Forcefield } = getPreferences();

    const compounds = (compoundsIn instanceof Array)
        ? compoundsIn
        : [compoundsIn];

    const { errorResult, caseData, connector } = getDataParentsForCompounds(compounds, 'Getting energies for');
    if (errorResult) {
        return errorResult;
    }

    // Mark all compounds as "energyRequested," before starting batches,
    // but use minimization requested to make the appearance the same as energy minimization.
    compounds.forEach((c) => c.energyInfo.minimizationRequested());
    EventBroker.publish('energyCalc');

    // Energy instructions for each batch of compounds
    const getEnergiesForOneBatch = async (batchCmpds) => {
        const compoundSpecs = batchCmpds.map((c) => c.resSpec);
        const requestArgs = {
            compoundSpecs,
            energyEngine: options.energyEngine || EnergyEngine,
            forcefield: options.forcefield || Forcefield,
            residueCutoff: options.residueCutoff || null,
            useUnboundConf: options.useUnboundConf != null ? options.useUnboundConf : undefined,
        };
        let responseData = await doWithActiveSolute(
            caseData, () => connector.cmdGetEnergiesForLigand(requestArgs)
        );

        // If MM fails, retry with AEnergy (disabled for now)
        if (failoverToAEnergy && responseData.hasError() && requestArgs.energyEngine === 'MM') {
            console.log(`MM Minimization failed: ${responseData.errors.join(', ')}. Retrying with BFD/AEnergy`);
            requestArgs.energyEngine = 'BFD';
            responseData = await doWithActiveSolute(
                caseData, () => connector.cmdGetEnergiesForLigand(requestArgs)
            );
        }

        const energies = integrateEnergies(responseData, caseData);
        const errors = responseData.byId(ResponseIds.Error);

        // Now get solvation
        if (!options.skipSolvation) {
            const solvationResult = await UserActions.GetSolvation(batchCmpds);
            const { energies: solvationEnergies, errors: solvationErrs } = solvationResult;
            if (solvationEnergies?.length > 0) energies.push(...solvationEnergies);
            if (solvationErrs?.length > 0) errors.push(...solvationErrs);
        }

        const cmpdErrs = gatherEnergyErrors(batchCmpds, null, energies, errors, options);
        cmpdErrs.forEach((arr, cmpd) => {
            if (arr.length > 0) {
                cmpd.energyInfo.energyError(EnergyInfo.Types.vdW, `Energy error: ${arr.join('; ')}`);
            }
        });

        // Clear the minimization request flag. It's ok to mark the minimization as success;
        // any errors should be reported with the specific energies.
        batchCmpds.forEach((cmpd) => cmpd.energyInfo.updateMinimizationRequest(true));
        EventBroker.publish('energyCalc');
        return { energies, errors };
    };

    // Kick off the energy requests in batches
    const { energies, errors } = await runInBatches({
        batchFn: getEnergiesForOneBatch,
        items: compounds,
        batchSize: options.batchSize || defaultEnergyBatchSize,
        label: 'energies',
    });
    return { compounds, energies, errors };
}

async function getSolvation(compoundsIn) {
    const compounds = ensureArray(compoundsIn);
    const { errorResult, caseData, connector } = getDataParentsForCompounds(compounds, 'Getting solvation for');
    if (errorResult) {
        return errorResult;
    }

    const compoundSpecs = compounds.map((c) => c.resSpec);
    compounds.forEach((c) => c.energyInfo.energyRequested(EnergyInfo.Types.ddGs));
    // Called standalone from the Selector menus, with no batch around it to publish for us.
    EventBroker.publish('energyCalc');

    let energies = [];
    let errors = [];
    try {
        const responseData = await doWithActiveSolute(
            caseData, () => connector.cmdGetSolvationForLigand({ compoundSpecs })
        );
        energies = integrateEnergies(responseData, caseData);
        errors = responseData.byId(ResponseIds.Error);
    } catch (ex) {
        // Names every spec, because gatherEnergyErrors attributes a plain-string error to a
        // compound only when the text contains its resSpec. Without this the settle below
        // falls to that function's own default and the rejection's cause is lost.
        errors = [`Solvation request failed for ${compoundSpecs.join(', ')}: ${ex?.message || ex}`];
        throw ex;
    } finally {
        // A failed or rejected request carries no solvation packet, so ddGs would stay
        // `working` and spin forever. Settle it with the server's error for that compound.
        const errMap = gatherEnergyErrors(compounds, null, energies, errors);
        for (const cmpd of compounds) {
            const ddGs = cmpd.energyInfo.getEnergyEntryByType(EnergyInfo.Types.ddGs);
            if (ddGs?.status === EnergyInfo.States.working) {
                cmpd.energyInfo.energyError(
                    EnergyInfo.Types.ddGs,
                    errMap.get(cmpd)?.join('; ') || 'No solvation energy returned'
                );
            }
        }
        // After the loop, not before. Not user-visible today -- the subscribers only schedule
        // renders and React runs those after this synchronous block -- but a publish ahead of
        // the settle is one scheduling change away from painting the stale spinner.
        EventBroker.publish('energyCalc');
    }

    return { compounds, energies, errors };
}
/**
 * @param {Compound | Compound[]} compoundsIn
 * @param {*} [options]
 * @returns {Promise<{
 *  energies: ReturnType<integrateEnergies>
 *  compounds: Compound[]
 *  errors: string[]
 * }>}
 */
async function energyMinimize(compoundsIn, options={}) {
    const { MinimizationEngine, EnergyEngine, Forcefield } = getPreferences();

    const compounds = (compoundsIn instanceof Array)
        ? compoundsIn
        : [compoundsIn];

    const { errorResult, caseData, connector } = getDataParentsForCompounds(compounds, 'Minimizing');
    if (errorResult) {
        return errorResult;
    }

    const okToMinimize = compounds; // Allow all compounds to be minimized; warn about clashes after
    // Mark all compounds as "minimizationRequested," before starting batches
    okToMinimize.forEach((c) => c.energyInfo.minimizationRequested());
    EventBroker.publish('energyCalc');

    // Minimization instructions for each batch of compounds
    const minimizeOneBatch = async (batchCmpds) => {
        const compoundSpecs = batchCmpds.map((c) => c.resSpec);
        const requestArgs = {
            compoundSpecs,
            minimizationType: options.minimizationType || 'Rigid',
            minimizationEngine: options.minimizationEngine || MinimizationEngine,
            energyEngine: options.energyEngine || EnergyEngine,
            forcefield: options.forcefield || Forcefield,
            minimizationSteps: options.minimizationSteps || null,
            residueCutoff: options.residueCutoff || null,
            useUnboundConf: options.useUnboundConf != null ? options.useUnboundConf : undefined,
        };
        // When the user opts into the in-browser engine, route the interactive
        // MM minimization to localMinimize() instead of the bfd-server ->
        // mm-server round-trip. The 'Local' preference is meaningful only for
        // what would otherwise be the MM path; the AEnergy/BFD server path is
        // unchanged. The in-browser module is reached via Loader.LocalMinimizer
        // (loaded only in dev/qa builds); when it is absent (prod) or 'Local'
        // is not selected, the server path runs -- so a stale persisted 'Local'
        // pref in a build without the module falls back safely.
        //
        // The local engine is not wrapped in doWithActiveSolute here: it awaits
        // extensively (param fetch, receptor prep, the minimize loop) before it
        // reaches a connector call, which would violate the helper's same-turn
        // contract and silently reopen the BP2-2663 window. It applies the helper
        // at its own send sites instead (server/activeSolute.js).
        const useLocal = requestArgs.minimizationEngine === 'Local' && Loader.LocalMinimizer;
        let responseData = useLocal
            ? await Loader.LocalMinimizer.localMinimize(
                batchCmpds, requestArgs, { caseData, connector },
            )
            : await doWithActiveSolute(
                caseData, () => connector.cmdEnergyMinimize(requestArgs)
            );

        // MM -> BFD failover stays scoped to non-Local engines. The condition
        // `minimizationEngine === 'MM'` already excludes 'Local', so a
        // Local-engine failure never auto-fails-over to the server; it surfaces
        // as EnergyInfo.failure instead.
        if (failoverToAEnergy && responseData.hasError() && requestArgs.minimizationEngine === 'MM') {
            console.log(`MM Minimization failed: ${responseData.errors.join(', ')}. Retrying with BFD/AEnergy`);
            requestArgs.minimizationEngine = 'BFD';
            requestArgs.energyEngine = 'BFD';
            responseData = await doWithActiveSolute(
                caseData, () => connector.cmdEnergyMinimize(requestArgs)
            );
        }

        const minCompounds = filterNonCompounds(
            responseData.byId(ResponseIds.Compound), 'energyMinimize'
        );
        // The Local engine mutates the existing compounds' coordinates in place
        // (localUpdatedInPlace), so addReplaceCompounds would destructively
        // self-replace a compound by its own spec (getState/setState/remove on
        // the same object wipes its display state). Instead, refresh the 3D
        // viewer atoms in place: remove + re-add the same atom objects so the
        // canvas picks up the new coordinates, without touching atomGroupState.
        // The server path returns brand-new compound objects and needs the
        // full addReplaceCompounds.
        if (responseData.localUpdatedInPlace) {
            for (const c of minCompounds) {
                App.Workspace.removeObject3dAtoms(c);
                App.Workspace.addObject3dAtoms(c);
            }
        } else {
            App.Workspace.addReplaceCompounds(minCompounds, caseData);
        }
        integrateCompounds(responseData, caseData);
        const energies = integrateEnergies(responseData, caseData);
        const errors = responseData.byId(ResponseIds.Error);

        // Now get solvation
        if (!options.skipSolvation && minCompounds.length > 0) {
            const solvationResult = await UserActions.GetSolvation(minCompounds);
            const { energies: solvationEnergies, errors: solvationErrs } = solvationResult;
            if (solvationEnergies?.length > 0) energies.push(...solvationEnergies);
            if (solvationErrs?.length > 0) errors.push(...solvationErrs);
        }

        if (hasPreviewModeError(errors)) {
            // Don't bother showing the error indicator for minimization in Preview mode
            // User should already see energies and will see the Login screen
            // However, if we support adding compounds in static mode, we should show
            // the error.
            batchCmpds.forEach((c) => c.energyInfo.updateMinimizationRequest(true));
        } else {
            minCompounds.forEach((c) => {
                // Don't clobber a per-compound failure already recorded for THIS run -- e.g.
                // the in-browser engine flagging non-convergence in applyEnergiesToCompound,
                // or a clash. minimizationRequested() cleared any prior-run error at the start
                // (~line 757), so an error state here is from this run and its warning must
                // survive (gatherEnergyErrors below only ADDS server-reported errors).
                if (c.energyInfo.minimizationStatus !== EnergyInfo.States.error) {
                    c.energyInfo.updateMinimizationRequest(true);
                }
                const heritageDetail = { ...requestArgs };
                delete heritageDetail.compoundSpecs;
                CompoundHeritage.addMinimize(c, heritageDetail);
            });
            const cmpdErrs = gatherEnergyErrors(
                batchCmpds, minCompounds,
                energies, errors, options
            );
            cmpdErrs.forEach((arr, cmpd) => {
                if (arr.length > 0) {
                    cmpd.energyInfo.updateMinimizationRequest(false, `Minimization error: ${arr.join('; ')}`);
                }
            });
        }
        EventBroker.publish('energyCalc');
        return { compounds: minCompounds, energies, errors };
    };
    // If none of the selected are able to be minimize, don't send the request
    if (okToMinimize.length === 0) return { compounds: [], energies: [], errors: [] };
    // Kick off the minimization requests in batches
    const minimizationResults = await runInBatches({
        batchFn: minimizeOneBatch,
        items: okToMinimize,
        batchSize: options.batchSize || defaultEnergyBatchSize,
        label: 'minimization',
    });

    reportClashingCompounds(minimizationResults.compounds, {
        alertParams: {
            singularNoun: 'minimized compound',
            pluralNoun: 'minimized compounds',
        },
    });

    return minimizationResults;
}

/**
 * @description Break up a list of items into batches, and run a function
 * on each batch, returning an object with all the results.
 * @param {*} batchFn - a function taking a list of items and returning an object.
 * @returns an object with fields aggregated from all the batch results
 */
async function runInBatches({
    items: itemsIn, batchFn, batchSize, label: labelIn='',
}={}) {
    const items = (itemsIn instanceof Array) ? itemsIn : [itemsIn];
    const label = labelIn ? `${labelIn} ` : labelIn;

    const ret = {};
    const batches = batchItems(items, batchSize);
    for (const [batchI, batchCmpds] of batches.entries()) {
        console.log(`Running ${label}batch ${batchI+1} / ${batches.length}`);
        const results = await batchFn(batchCmpds);
        for (const [rKey, rValue] of Object.entries(results)) {
            if (!ret[rKey]) ret[rKey] = [];
            ret[rKey].push(...rValue);
        }
    }
    return ret;
}

/**
 * @description Break up a list of items into a list of lists, each with a
 * max number of items
 */
function batchItems(items, batchSizeIn=10) {
    const batchSize = (batchSizeIn === -1) ? items.length : batchSizeIn;
    let working = [];
    const batches = [working];
    for (const compound of items) {
        if (working.length === batchSize) {
            working = [];
            batches.push(working);
        }
        working.push(compound);
    }
    return batches;
}

/**
 * Get GIFE energies for atoms or atomGroups
 * @param {{ atoms: Atom[], atomGroups: AtomGroup[]}} param0
 * @returns {Promise<{ results: GiFEResultUnit[], error: string }}
 */
async function getGifeEnergies({ atoms, atomGroups }={}) {
    const gifeRequestObj = await prepareGiFERequest({ atoms, atomGroups });
    const { results, error } = await sendGiFERequest(gifeRequestObj);
    return { results, error };
}

/**
 * @param {Compound[] | Compound} compounds
 */
async function updateGifeEnergies(compounds) {
    return updateGifeEnergiesForCompounds(compounds);
}

/**
 * startFepScoringCmd
 * Submit a relative free-energy (FEP) scoring job for a set of compounds. The
 * compute runs on the cpaws GPU fleet (OpenFE or TMD); this returns once the
 * job is queued (it is NOT awaited — FEP campaigns run for hours). On success
 * the job appears in the Jobs list and writes per-compound ΔG when complete.
 * @param {Compound[] | Compound} compoundsIn
 * @param {import('BMapsSrc/integrations/fep').startFepScoring} [opts] - FEP options
 *   (program, mode, runOptimization, network, jobName).
 * @returns {Promise<{ jobid?: string, error?: string, shortfall?: object }>}
 */
/**
 * Submit a REINVENT generative-design job for the given compounds.
 *
 * Exists because `job-reinvent` could not be dispatched at all. It declared three inputs --
 * `jobName`, `smilesList` and `objectives` -- and the server takes none of them:
 * `ReinventJob.buildRequestPayload` posts `{ smiles }` and nothing else, and the job's name is a
 * static on the class (`ReinventJob.jobName`), not a user value. `objectives` appeared nowhere in
 * the repo outside its own declaration. Two of the three being required meant every plan naming
 * job-reinvent was refused as invalid, by the planner and by all three de-novo phrasings.
 *
 * It also needs a command rather than a raw `jobType`, for the same reason `job-fep-scoring` has
 * one: `dispatchJobSubmission` posts `{...params}` straight through, which would skip BOTH things
 * `buildRequestPayload` does -- taking only the first whitespace token of each line, the `.smi`
 * convention REINVENT does not accept, and checking bracketed tokens against the set its
 * mol2mol_scaffold_generic model allows. A plan cannot carry SMILES text either; it carries
 * compounds, so the mapping has to happen somewhere.
 *
 * Takes no options argument on purpose: the ActionDef declares `parameters: []` because the
 * server's request has exactly one field, so there is nothing for a second argument to carry.
 *
 * @param {Compound[] | Compound} compoundsIn - Starting molecules
 * @returns {Promise<{jobId?: string, jobType?: string, state?: string, errors?: string[],
 *   error?: string}>}
 */
/**
 * Request MapLight ADMET predictions for compounds, and hand the values back.
 *
 * The same fix as job-reinvent, and the one dispatchTargets.test.js's own note asked for: the
 * ActionDef declared `compoundDict: object`, described as `{cmpdId: {smiles}}`, which is the
 * shape `getMapLightProperties` BUILDS internally -- and the server field is `data`, not
 * `compoundDict`. A plan cannot carry that dict either; it carries compounds. So every plan
 * naming job-maplight was refused as uncarryable, while a working path that takes compounds and
 * does the reshaping already existed.
 *
 * Routed through a command rather than a bare `jobType` for the same reason as REINVENT:
 * `dispatchJobSubmission` would post `{...params}` straight through, skipping the dedup by
 * unique structure id, the already-cached skip, and `fetchPropertyGroupWithJobman`, which is
 * what waits for the job, integrates the values onto the compounds and then deletes the job.
 *
 * Not fire-and-forget despite `runtime`: the underlying call awaits completion, which is why
 * `predictions` can be returned at all and why no `jobId` is -- the job is removed once its
 * results are integrated, so an id would name something that no longer exists.
 *
 * @param {Compound[] | Compound} compoundsIn
 * @param {{recalculate?: boolean}} [opts]
 * @returns {Promise<{predictions?: object, cached?: boolean, error?: string}>}
 */
/**
 * Submit a library (selectivity) screen for the given compounds.
 *
 * 🔑 A screening "library" is a panel of protein TARGETS, not a compound library. You submit your
 * compounds and they are scored against every item in the panel -- `all-human-kinases` is 536
 * distinct kinases. So the protein loaded in the workspace has nothing to do with this job, and
 * WHICH library is a chemistry decision rather than a default: screening against human kinases
 * whatever target is loaded would be a wrong answer delivered confidently. `library` is therefore
 * required with no default, so an unqualified request asks instead of guessing.
 *
 * The ActionDef declared `jobName`, `compounds`, `targets: object` and `screenSpec: object`. Three
 * of those are wrong: the API takes no `targets` (the library IS the targets) and no `screenSpec`,
 * and neither object was carryable by a plan, so every plan naming the action was refused. Ground
 * truth for the payload is `ScreeningPane/Tabs/NewScreen.jsx`, the only path that submits this
 * job successfully, and this builds the same request it does.
 *
 * Library and screen-type keys come from `jobman/screeningLibraries.js` rather than being
 * repeated here -- `getLibraryOptions()` is a lazy accessor because lab features add a third
 * library in dev, so the valid set differs between environments and a constant would be wrong in
 * one of them.
 *
 * @param {Compound[] | Compound} compoundsIn - Compounds to screen
 * @param {{library?: string, screenType?: string, jobName?: string}} [opts]
 * @returns {Promise<{jobId?: string, library?: string, error?: string}>}
 */
/**
 * Score compounds with GNINA's CNN scoring function, and hand the scores back.
 *
 * The callee for `job-gnina-scoring`, whose description is the accurate one: "fast evaluation
 * without generating new poses". That is `score_only: true`, which is exactly what
 * getGninaScoringProperties asks for. Its ActionDef had declared `receptorPdb` and `ligandSdf` --
 * the snake_cased fields `makeGninaRequest` BUILDS internally from the compounds via
 * GetProteinPdb and Compound.makeSDF, not inputs a caller supplies.
 *
 * ⚠️ NOT a replacement for `predict-gnina-scores`, which is a different operation despite the
 * name: its `skipDocking` parameter defaults to FALSE, so it promises dock-then-score. That one
 * is separately mis-bound -- it declares CNN scores but calls `ScoreCompounds`, which is BMaps'
 * own interaction scoring in ScoringCmds.js. It is blocked on 'callee-shape' so it never ran and
 * the mis-binding never showed. Left for a decision rather than guessed at here.
 *
 * @param {Compound[] | Compound} compoundsIn - Compounds whose poses to score
 * @param {{scoring?: string, cnn_scoring?: string, cnn?: string}} [opts] - GNINA knobs, which
 *   reach the request through makeGninaRequest's `...other` spread
 * @returns {Promise<{scores?: object, error?: string, partialErrors?: string[]}>}
 */
/**
 * `predict-gnina-scores`: GNINA CNN scores for compounds, re-docking first unless told not to.
 *
 * It was MIS-BOUND to `ScoreCompounds`, which is BMaps' own interaction scoring in ScoringCmds.js
 * -- so the action promised CNN affinity and confidence and would have produced vdW + h-bond
 * interaction scores instead. It was blocked on 'callee-shape', so it never ran and the
 * mis-binding never surfaced. That is also why its declaration looked fine: the inputs and
 * outputs were right all along; only the callee was wrong.
 *
 * Both branches are deliberate, per the PhIIB SME decision ("GNINA scoring must NOT re-dock"):
 *   - `skipDocking: true` scores the poses as they are. Cascades that compose dock-then-score,
 *     like dockAndScoreShortcut, must pass this, so the scoring step does not re-run docking on
 *     compounds a previous step already docked.
 *   - `skipDocking: false`, the registry default, re-docks first. Kept for single-action callers,
 *     who may be scoring compounds that were never docked -- scoring those as-is would score an
 *     arbitrary conformation.
 *
 * @param {Compound[] | Compound} compoundsIn
 * @param {{skipDocking?: boolean, boxParams?: Object, speedArg?: string}} [opts] - remaining
 *   options go to the GNINA request (score-only) or to the docking call
 * @returns {Promise<{scores?: Object, dockedPoses?: Compound[], error?: string,
 *   errors?: string[]}>}
 */
async function predictGninaScoresCmd(compoundsIn, { skipDocking=false, ...gninaOptions }={}) {
    const compounds = ensureArray(compoundsIn).filter(Boolean);
    if (compounds.length === 0) {
        return { error: 'GNINA scoring needs at least one compound, and none was supplied.' };
    }

    if (skipDocking) {
        // Score the existing poses. Same path as job-gnina-scoring, which is score-only by
        // definition -- so the two actions share a callee here rather than duplicating it.
        return getGninaScoresCmd(compounds, gninaOptions);
    }

    // Re-dock, then report the poses. The scores ride on them: the docking job's results are
    // integrated as scoped properties under `gnina_docking`, which is how the compound table and
    // the ranker already read them. Deliberately not re-derived into a `scores` map here --
    // reading them back through getProperty would duplicate the job's own integration and drift
    // from it when the property set changes.
    const { compounds: dockedPoses, errors } = await dockWithJobManager(
        compounds, { dockingProgram: 'gnina', ...gninaOptions },
    );
    if (errors?.length > 0 && !(dockedPoses?.length > 0)) {
        return { error: `GNINA docking failed: ${errors.join('; ')}` };
    }
    return { dockedPoses, errors: errors?.length > 0 ? errors : undefined };
}

async function getGninaScoresCmd(compoundsIn, opts={}) {
    const compounds = ensureArray(compoundsIn).filter(Boolean);
    if (compounds.length === 0) {
        return { error: 'GNINA scoring needs at least one compound, and none was supplied.' };
    }

    // makeGninaRequest refuses a mixed selection ("needs all compounds from the same protein
    // structure") and needs a protein to score against, so say so here rather than letting it
    // surface as a bare request error.
    const { dataParentsList } = App.partitionByDataParents(compounds);
    if (dataParentsList.length > 1) {
        return {
            error: 'GNINA scores compounds against one protein structure at a time, and '
                + `${joinCompoundSpecs(compounds)} come from more than one.`,
        };
    }

    const result = await getGninaScoringProperties(compounds, opts);
    if (result?.error) return { error: `GNINA scoring failed: ${result.error}` };
    return { scores: result?.itemValueMap, partialErrors: result?.partialErrors };
}

async function startLibraryScreenCmd(compoundsIn, { library, screenType, jobName }={}) {
    const compounds = ensureArray(compoundsIn).filter(Boolean);
    if (compounds.length === 0) {
        return { error: 'A library screen needs at least one compound, and none was supplied.' };
    }

    const chosenLibrary = findLibrary(library);
    if (!chosenLibrary) {
        const valid = getLibraryOptions().map((o) => `${o.key} (${o.label})`).join(', ');
        return {
            error: library
                ? `Unknown screening library "${library}". Choose one of: ${valid}.`
                : `Which library should I screen against? Choose one of: ${valid}.`,
        };
    }

    // One screen type exists today, so defaulting it is safe in a way defaulting the library is
    // not: there is nothing else it could have meant.
    const chosenType = findScreenType(screenType || screenTypeOptions[0].key);
    if (!chosenType) {
        return {
            error: `Unknown screen type "${screenType}". Choose one of: `
                + `${screenTypeOptions.map((o) => o.key).join(', ')}.`,
        };
    }

    // NewScreen maps each staged row to {smiles, id} and notes "we convert the name to id when
    // submitting", with duplicates blocked by disabling Submit. resSpec is the workspace's own
    // unique handle for a compound, so it cannot collide the way a display name can.
    const input = compounds
        .map((cmpd) => ({ smiles: cmpd.getUnnamedSmiles?.(), id: cmpd.resSpec }))
        .filter((entry) => entry.smiles && entry.id);
    if (input.length === 0) {
        return {
            error: `No SMILES available for ${joinCompoundSpecs(compounds)}, so there is nothing `
                + 'to screen.',
        };
    }

    const { results, error } = await createJob(JobTypes.LIBRARY_SCREEN, {
        compounds: input,
        library: { id: chosenLibrary.key },
        screen_type: { jobtype: chosenType.jobtype, subtype: chosenType.subtype },
        jobName: jobName || `Screen ${input.length} compound(s) vs ${chosenLibrary.label}`,
    });
    if (error) return { error: `Library screen submission failed: ${error}` };

    return { jobId: results?.jobid, library: chosenLibrary.key };
}

async function getMapLightPredictionsCmd(compoundsIn, { recalculate }={}) {
    const compounds = ensureArray(compoundsIn).filter(Boolean);
    if (compounds.length === 0) {
        return { error: 'MapLight needs at least one compound, and none was supplied.' };
    }
    const withSmiles = compounds.filter((cmpd) => cmpd.getUnnamedSmiles?.());
    if (withSmiles.length === 0) {
        return {
            error: `No SMILES available for ${joinCompoundSpecs(compounds)}, and MapLight `
                + 'predicts from SMILES.',
        };
    }

    const result = await getMapLightProperties(withSmiles, { recalculate });
    if (result?.error) return { error: `MapLight prediction failed: ${result.error}` };
    if (result?.alreadyCached) {
        // The values are already on the compounds, so this is a success, not an empty result.
        // `recalculate: true` forces a refetch.
        return { predictions: {}, cached: true };
    }
    return { predictions: result?.itemValueMap };
}

async function startReinventGenerationCmd(compoundsIn) {
    const compounds = ensureArray(compoundsIn).filter(Boolean);
    if (compounds.length === 0) {
        return { error: 'REINVENT needs at least one starting compound, and none was supplied.' };
    }

    // getUnnamedSmiles is what the job's own form widget uses on each compound, so the chat route
    // and the form route hand the server the same thing.
    const smiles = compounds.map((cmpd) => cmpd.getUnnamedSmiles?.()).filter((s) => s);
    if (smiles.length === 0) {
        return {
            error: `No SMILES available for ${joinCompoundSpecs(compounds)}, so there is nothing `
                + 'REINVENT can start from.',
        };
    }

    // The job's own validation, not a copy of it: a second implementation would drift from the
    // model's allowed-token set.
    const errors = ReinventJob.validateSmiles(smiles);
    if (errors.length > 0) return { errors };

    const { results, error } = await postJob({
        jobtype: 'reinvent',
        jobrequest: { smiles },
        stash: { actionId: 'job-reinvent', actionName: 'REINVENT Generative Design Job' },
    });
    if (error) return { error: `REINVENT submission failed: ${error}` };

    return {
        jobId: results?.jobid,
        jobType: 'reinvent',
        state: results?.state || 'starting',
    };
}

async function startFepScoringCmd(compoundsIn, opts={}) {
    const {
        results, error, program, shortfall,
    } = await startFepScoring(compoundsIn, opts);
    // Too few ligands for a per-compound ΔG, and the caller has not agreed to
    // it: nothing was submitted. Say what they WOULD get and how to proceed,
    // rather than reporting a flat failure — the run is legitimate, it just
    // answers a narrower question. The rule's wording comes from
    // `fepDgShortfall` so the form and the chat say the same thing; only the
    // proceed-anyway affordance is added here, because only this route has one.
    if (shortfall) {
        // Do NOT offer "run it anyway" here. That affordance needs a pending
        // acknowledgement armed in the chat UI, which sets
        // `acknowledgeDgShortfall` on the retry; it exists on the fork line but
        // NOT in this tree's ChatBot. Offering it anyway sent the next message
        // to the planner with no memory of this refusal, which classified four
        // stray words on their own and dispatched something unrelated —
        // observed on bazaar 2026-10-13, where "run it anyway" opened the
        // sketcher. A refusal must only offer what this build can honour, so
        // the two routes named below are ones the user can take right now.
        showAlert(
            'FEP',
            `${shortfall.message}\n\nI have not submitted anything. Add compounds until at `
            + `least ${MIN_COMPOUNDS_FOR_DG} are loaded and ask again for a per-compound ΔG, or `
            + 'ask for an absolute calculation to get a ΔG for a single compound.'
        );
        // Deliberately NOT `{ error, shortfall }`. A shortfall is a refusal with
        // a resume path, not a failure, and returning `error` alongside it makes
        // the dispatcher discard the result entirely: `recordActionResult`
        // (`gibbsDispatch.js:99`) pushes `result.error` into the plan-level
        // errors and RETURNS before `results.results.push(entry)`. The caller
        // then sees no action result at all, so the chat's shortfall branch —
        // the one that offers "run it anyway" and arms the pending
        // acknowledgement — was unreachable, and the user got a flat "I could
        // not start the free-energy calculation" instead.
        return { shortfall };
    }
    if (error) {
        showAlert('FEP', `Could not start FEP job: ${error}`);
        return { error };
    }
    showAlert(
        'FEP',
        `Submitted ${program} job ${results?.jobid || ''}. It runs on `
        + `${opts.target === 'conifergpu2' ? 'ConiferGPU2' : 'the compute fleet'}; watch it in the `
        + 'Free Energy panel, where results can be viewed and applied to compounds when complete.'
    );
    return { jobid: results?.jobid, program };
}

/**
 * applyFepScoringResultsCmd
 * Apply a completed FEP job's per-ligand ΔG onto the matching workspace
 * compounds (matched by ligand name). Use after a `StartFepScoring` job shows
 * complete in the Jobs list.
 * @param {string} jobid
 * @param {{ compounds?: Compound[] }} [opts]
 * @returns {Promise<import('BMapsSrc/jobman/JobDefinition').IntegrateResultsReturn>}
 */
async function applyFepScoringResultsCmd(jobid, opts={}) {
    const result = await applyFepScoringResults(jobid, opts);
    if (result?.error) {
        showAlert('FEP', `Could not apply FEP results: ${result.error}`);
    }
    return result;
}

/**
 * updateExternalPropertiesForCompounds
 * Fetch external properties (MapLight ADMET and Gnina scoring) for the specified compounds.
 * @param {Compound[] | Compound} compoundsIn
 * @param {{recalculate: boolean,
 * includeProps?: string[], excludeProps?: string[],
 * trigger?: () => void,
 * }} [options]
 */
export async function updateCompoundExternalProps(compoundsIn, options={}) {
    const compounds = ensureArray(compoundsIn);
    const { recalculate, trigger } = options;

    let toFetch = ['maplight', 'gnina_scoring'];
    if (options.includeProps) {
        toFetch = [...options.includeProps];
    }
    if (options.excludeProps) {
        toFetch = toFetch.filter((p) => !options.excludeProps.includes(p));
    }

    const promises = [];
    if (toFetch.includes('maplight')) {
        promises.push(getMapLightProperties(compounds, {
            trigger,
            recalculate,
        }));
    }

    if (toFetch.includes('gnina_scoring')) {
        promises.push(
            getGninaScoringProperties(compounds, {
                trigger,
                // gnina function doesn't support recalculate flag
            })
        );
    }

    await Promise.all(promises);
}
